import { useEffect, useMemo, useState } from "react";
import LoadingCircle from "../utility/LoadingCircle";
import FieldConverter from "./inputtypes/FieldConverter";
import { AiFillWarning } from "react-icons/ai";
import SolarGradeInput from "./inputtypes/SolarGradeInput";
import { MdError, MdMap } from "react-icons/md";
import { FaMapMarkerAlt } from "react-icons/fa";
import MissingSolarGradeWarning from "./buttons/MissingSolarGradeWarning";

const ProjectSection = ({
	api,
	token,
	data,
	projectId,
	conditionalData,
	setConditionalData,
	projectName,
	templateId,
	templateName,
	sectionId,
	stage,
	templateTypeId,
	projectHtmlId,
	requiredFieldsBySection,
	setRequiredFieldsBySection,
	aerialMarkersMissingBySection,
	setAerialMarkersMissingBySection,
	aerialMarkersReady,
	showThisSection,
	initialThumbnailPathes,
	setLastModifiedOn,
	showElement,
	selectedOdooModel,
	setLastUpdatedBy,
	odooInstallationName,
	odooParentName,
	isGuest,
	keyedSolarGradeData,
	solarGradeIds,
	keyedKrummySolarGradeData,
	setSolarGradeFilteredSections,
	startingPendingImages,
	focusedFieldId,
	hasAerialView,
	aerialMarkerFieldIds,
	onShowOnAerial,
	onFieldDataChange,
}) => {
	const [fields, setFields] = useState([]);
	const [fieldData, setFieldData] = useState([]);
	const [odooMappings, setOdooMappings] = useState([]);
	const [odooId, setOdooId] = useState(-1);
	const [isLoading, setIsLoading] = useState(false);
	const [requiredFieldsRemaining, setRequiredFieldsRemaining] = useState([]);
	const [showOnlyRequiredRemaining, setShowOnlyRequiredRemaining] = useState(false);
	const [aerialMarkersMissingRemaining, setAerialMarkersMissingRemaining] = useState([]);
	const [showOnlyAerialMarkerRemaining, setShowOnlyAerialMarkerRemaining] = useState(false);
	const [missingSolarGradeKeys, setMissingSolarGradeKeys] = useState([]);

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;
		setIsLoading(true);
		fetch(api + "/project/section/" + data.id + "?project_id=" + projectId, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + token,
			},
			signal,
		})
			.then((res) => {
				if (!res.ok) {
					setIsLoading(false);
					// put an error here
				} else {
					return res.json();
				}
			})
			.then((res) => {
				if (res) {
					setIsLoading(false);
					const unsortedFields = res.fields;
					const sortedFields = unsortedFields.sort((a, b) => a.position - b.position);

					setFields(sortedFields);
					setFieldData(res.projectData);
					setOdooMappings(res.odooMappings);
					setOdooId(res.odooId);
					if (onFieldDataChange) {
						for (const field of sortedFields) {
							const projectField = res.projectData.find((fieldDataItem) => fieldDataItem.field_id === field.id);
							const odooMapping = res.odooMappings.find(
								(mapping) => mapping.templatefieldid === field.id && mapping.odoo_value !== "failedtogetdataforpmscode400",
							);
							onFieldDataChange(field.id, {
								value: odooMapping ? odooMapping.odoo_value : projectField?.value ?? "",
								comment: projectField?.comment ?? "",
								projectFieldId: projectField?.id || null,
								conditional_list: field.conditional_list ?? "",
								field_type: field.field_type,
							});
						}
					}
				} else {
					setIsLoading(false);
				}
			})
			.catch((err) => {});

		return () => {
			controller.abort();
		};
	}, [api, token, data.id, projectId, onFieldDataChange]);
	useEffect(() => {
		for (let i = 0; i < conditionalData.length; i++) {
			if (data.id === conditionalData[i].section_condition_id && conditionalData[i].conditionMet === false) {
				for (let j = 0; j < fieldData.length; j++) {
					if (fieldData[j].field_id === conditionalData[i].field_condition_id) {
						// rather than using the field data id, we first check if there is an odoo mapping
						let currentFieldvalue = fieldData[j].value;
						let filteredOdoOMappings = odooMappings.filter((filterData) => filterData.templatefieldid === fieldData[j].field_id);
						if (filteredOdoOMappings.length > 0) {
							if (filteredOdoOMappings[0].odoo_value !== "failedtogetdataforpmscode400") {
								currentFieldvalue = convertToYesNo(filteredOdoOMappings[0].odoo_value);
							}
						}

						let conditionMet = false;
						if (conditionalData[i].condition_type === ">=") {
							if (parseInt(currentFieldvalue) >= parseInt(conditionalData[i].condition_value)) {
								conditionMet = true;
							}
						} else if (conditionalData[i].condition_type === ">") {
							if (parseInt(currentFieldvalue) > parseInt(conditionalData[i].condition_value)) {
								conditionMet = true;
							}
						} else if (conditionalData[i].condition_type === "=") {
							if (currentFieldvalue == conditionalData[i].condition_value) {
								conditionMet = true;
							}
						} else if (conditionalData[i].condition_type === "<") {
							if (parseInt(currentFieldvalue) < parseInt(conditionalData[i].condition_value)) {
								conditionMet = true;
							}
						} else if (conditionalData[i].condition_type === "<=") {
							if (parseInt(currentFieldvalue) <= parseInt(conditionalData[i].condition_value)) {
								conditionMet = true;
							}
						}
						if (conditionMet) {
							let copyOfData = conditionalData;
							copyOfData[i].conditionMet = true;
							setConditionalData([...copyOfData]);
						}
					}
				}
			}
		}
	}, [conditionalData, data.id, fieldData, odooMappings, setConditionalData]);

	useEffect(() => {
		if (requiredFieldsRemaining.length === 0) {
			setShowOnlyRequiredRemaining(false);
		}
	}, [requiredFieldsRemaining.length]);

	useEffect(() => {
		if (aerialMarkersMissingRemaining.length === 0) {
			setShowOnlyAerialMarkerRemaining(false);
		}
	}, [aerialMarkersMissingRemaining.length]);

	useEffect(() => {
		if (requiredFieldsRemaining.length !== requiredFieldsBySection[sectionId].length) {
			let tempRequiredFieldsBySection = { ...requiredFieldsBySection };
			tempRequiredFieldsBySection[sectionId] = requiredFieldsRemaining;
			setRequiredFieldsBySection(tempRequiredFieldsBySection);
		}
	}, [requiredFieldsBySection, requiredFieldsRemaining, sectionId, setRequiredFieldsBySection]);

	useEffect(() => {
		if (!aerialMarkersMissingBySection[sectionId]) {
			return;
		}
		if (aerialMarkersMissingRemaining.length !== aerialMarkersMissingBySection[sectionId].length) {
			let tempAerialMarkersMissingBySection = { ...aerialMarkersMissingBySection };
			tempAerialMarkersMissingBySection[sectionId] = aerialMarkersMissingRemaining;
			setAerialMarkersMissingBySection(tempAerialMarkersMissingBySection);
		}
	}, [aerialMarkersMissingBySection, aerialMarkersMissingRemaining, sectionId, setAerialMarkersMissingBySection]);

	useEffect(() => {
		const solarGradeMissingIds = fields
			.filter((field) => {
				if (field.field_type !== "solar grade" || !field.require_aerial_view_marker) {
					return false;
				}
				if (!aerialMarkersReady || aerialMarkerFieldIds.includes(field.id)) {
					return false;
				}
				if (!solarGradeIds || !solarGradeIds.visitId || !solarGradeIds.projectId) {
					return false;
				}
				const solarGradeKey = `${data.name} - ${field.name}`;
				if (!keyedSolarGradeData?.[solarGradeKey] || !["fail", "pass"].includes(keyedSolarGradeData[solarGradeKey].condition)) {
					return false;
				}
				return true;
			})
			.map((field) => field.id.toString());

		setAerialMarkersMissingRemaining((prev) => {
			const nonSolarIds = prev.filter((id) => {
				const field = fields.find((item) => item.id.toString() === id);
				return !field || field.field_type !== "solar grade";
			});
			const next = [...nonSolarIds];
			for (const id of solarGradeMissingIds) {
				if (!next.includes(id)) {
					next.push(id);
				}
			}
			if (next.length === prev.length && next.every((id) => prev.includes(id))) {
				return prev;
			}
			return next;
		});
	}, [aerialMarkerFieldIds, aerialMarkersReady, data.name, fields, keyedSolarGradeData, solarGradeIds]);

	const toggleShowOnlyRequired = (e) => {
		e.preventDefault();
		setShowOnlyRequiredRemaining((prev) => !prev);
		const sectionHTML = document.getElementById(projectHtmlId);
		sectionHTML.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const toggleShowOnlyAerialMarkerRequired = (e) => {
		e.preventDefault();
		setShowOnlyAerialMarkerRemaining((prev) => !prev);
		const sectionHTML = document.getElementById(projectHtmlId);
		sectionHTML.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const fieldMatchesActiveFilters = (fieldId) => {
		const fieldIdString = fieldId.toString();
		const matchesRequired = requiredFieldsRemaining.includes(fieldIdString);
		const matchesAerial = aerialMarkersMissingRemaining.includes(fieldIdString);
		if (!showOnlyRequiredRemaining && !showOnlyAerialMarkerRemaining) {
			return true;
		}
		if (showOnlyRequiredRemaining && showOnlyAerialMarkerRemaining) {
			return matchesRequired || matchesAerial;
		}
		if (showOnlyRequiredRemaining) {
			return matchesRequired;
		}
		return matchesAerial;
	};

	const hasValidSolarGradeInSection = useMemo(() => {
		if (!keyedSolarGradeData) return false;

		const prefix = `${data.name} - `;
		for (const [k, v] of Object.entries(keyedSolarGradeData)) {
			if (k.startsWith(prefix) && (v?.condition === "fail" || v?.condition === "pass")) {
				return true;
			}
		}
		return false;
	}, [keyedSolarGradeData, data?.name]);

	const convertToYesNo = (theValue) => {
		let returnValue = "";
		if (theValue === 1 || theValue === "1" || (typeof theValue === "string" && theValue.trim().toLowerCase() === "yes") || theValue === true || (typeof theValue === "string" && theValue.trim().toLowerCase() === "true")) {
			returnValue = "yes";
		} else if (theValue === 0 || theValue === "0" || (typeof theValue === "string" && theValue.trim().toLowerCase() === "no") || theValue === false || (typeof theValue === "string" && theValue.trim().toLowerCase() === "false")) {
			returnValue = "no";
		}
		return returnValue;
	};

	useEffect(() => {
		if (!keyedSolarGradeData) {
			setSolarGradeFilteredSections((prev) => prev.filter((id) => id !== data.id));
			return;
		}

		const prefix = `${data.name} - `;
		const supportedKeys = new Set(fields.map((field) => `${data.name} - ${field.name}`));

		const missingKeys = Object.keys(keyedSolarGradeData).filter((key) => key.startsWith(prefix) && !supportedKeys.has(key));

		setMissingSolarGradeKeys(missingKeys);

		if (hasValidSolarGradeInSection) {
			setSolarGradeFilteredSections((prev) => (prev.includes(data.id) ? prev : [...prev, data.id]));
		} else {
			setSolarGradeFilteredSections((prev) => prev.filter((id) => id !== data.id));
		}
	}, [hasValidSolarGradeInSection, keyedSolarGradeData, data.id, data.name, fields, setSolarGradeFilteredSections]);

	const containsFocusedField = fields.some((field) => field.id === focusedFieldId);

	if (solarGradeIds && solarGradeIds.visitId && solarGradeIds.projectId && !hasValidSolarGradeInSection && !containsFocusedField) {
		return null;
	}

	return (
		<div id={projectHtmlId} className={`${showThisSection || containsFocusedField ? "" : "hidden"}`}>
			{
				<div className="flex flex-col gap-3 mb-2 md:p-1" id={data.name.replaceAll(" ", "-")}>
					<div className="text-2xl font-bold sticky -top-1 bg-gray-200 p-1 flex flex-col md:flex-row gap-2 z-[1]">
						<p>{data.name}</p>

						<div className="flex gap-2">
							{showElement ? (
								requiredFieldsRemaining.length > 0 ? (
									<div
										className="flex items-center text-sm bg-yellow-300 p-1 rounded-lg gap-1 cursor-pointer select-none border border-black"
										title="show only the required fields"
										onClick={toggleShowOnlyRequired}>
										<AiFillWarning />
										<p>{requiredFieldsRemaining.length} </p>
									</div>
								) : null
							) : null}
							{showElement ? (
								requiredFieldsRemaining.length > 0 ? (
									showOnlyRequiredRemaining && requiredFieldsRemaining.length ? (
										<div
											className="flex items-center text-sm bg-gray-50 p-1 rounded-lg cursor-pointer select-none border border-black text-center"
											title="show only the required fields"
											onClick={toggleShowOnlyRequired}>
											<p>Show All</p>
										</div>
									) : (
										<div
											className="flex items-center text-xs bg-gray-50 p-1 rounded-lg cursor-pointer select-none border border-black text-center"
											title="show only the required fields"
											onClick={toggleShowOnlyRequired}>
											<p>Show Required Only</p>
										</div>
									)
								) : null
							) : null}
							{showElement ? (
								aerialMarkersMissingRemaining.length > 0 ? (
									<div
										className="flex items-center text-sm bg-orange-400 p-1 rounded-lg gap-1 cursor-pointer select-none border border-black"
										title="show only the aerial marker required fields"
										onClick={toggleShowOnlyAerialMarkerRequired}>
										<FaMapMarkerAlt />
										<p>{aerialMarkersMissingRemaining.length} </p>
									</div>
								) : null
							) : null}
							{showElement ? (
								aerialMarkersMissingRemaining.length > 0 ? (
									showOnlyAerialMarkerRemaining && aerialMarkersMissingRemaining.length ? (
										<div
											className="flex items-center text-sm bg-gray-50 p-1 rounded-lg cursor-pointer select-none border border-black text-center"
											title="show only the aerial marker required fields"
											onClick={toggleShowOnlyAerialMarkerRequired}>
											<p>Show All</p>
										</div>
									) : (
										<div
											className="flex items-center text-xs bg-gray-50 p-1 rounded-lg cursor-pointer select-none border border-black text-center"
											title="show only the aerial marker required fields"
											onClick={toggleShowOnlyAerialMarkerRequired}>
											<p>Show Aerial Marker Required Only</p>
										</div>
									)
								) : null
							) : null}
						</div>
					</div>
					{missingSolarGradeKeys && missingSolarGradeKeys.length > 0 ? (
						<MissingSolarGradeWarning missingKeys={missingSolarGradeKeys} sectionName={data.name} />
					) : null}
					{fields && fields.length > 0 ? (
						fields.map((mapData, index) => {
							if (mapData.field_type === "solar grade") {
								const hideSolarGradeForAerialFilter =
									showOnlyAerialMarkerRemaining &&
									!aerialMarkersMissingRemaining.includes(mapData.id.toString()) &&
									focusedFieldId !== mapData.id;
								if (hideSolarGradeForAerialFilter) {
									return null;
								}
								if (!solarGradeIds || !solarGradeIds.visitId || !solarGradeIds.projectId) {
									return (
										<div
											key={mapData.id}
											id={`field-${mapData.id}`}
											className={`bg-white rounded-md p-4 border-l-8 border-l-blue-500 ${
												focusedFieldId === mapData.id ? "ring-4 ring-blue-500 ring-offset-2" : ""
											}`}>
											<div className="flex gap-2 items-center">
												<p className="bg-gray-300 p-1 text-xs">Solar Grade</p>
												<p className="text-xl font-bold">{mapData.name}</p>
											</div>
											<div className="mt-2 flex items-center gap-2 bg-red-300 p-2 rounded-md">
												<MdError className="text-4xl" />
												<p>
													{`Warning: The Odoo Helpdesk Ticket (id: ${odooId}) associated with this project is missing the Solar Grade Visit Id (x_solar_grade_visit_id) needed to
													use this field. Please update the odoo record to use this field.`}
												</p>
											</div>
										</div>
									);
								}

								let solarGradeKey = `${data.name} - ${mapData.name}`;
								if (
									!keyedSolarGradeData[solarGradeKey] ||
									(keyedSolarGradeData[solarGradeKey] && !["fail", "pass"].includes(keyedSolarGradeData[solarGradeKey].condition))
								) {
									return focusedFieldId === mapData.id ? (
										<div
											key={mapData.id}
											id={`field-${mapData.id}`}
											className="rounded-md bg-yellow-100 p-4 ring-4 ring-blue-500 ring-offset-2">
											<p className="font-bold">{mapData.name}</p>
											<p className="text-sm">This Solar Grade field is currently unavailable because it has no pass/fail data.</p>
										</div>
									) : null;
								}

								return (
									<div
										key={mapData.id}
										id={`field-${mapData.id}`}
										className={focusedFieldId === mapData.id ? "rounded-lg ring-4 ring-blue-500 ring-offset-2" : ""}>
										{hasAerialView ? (
											<div className="mb-1 flex justify-end">
												<button
													type="button"
													className="flex items-center gap-1 rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700"
													onClick={() => onShowOnAerial(mapData.id)}>
													<MdMap />
													{aerialMarkerFieldIds.includes(mapData.id) ? "Show marker" : "Aerial View"}
												</button>
											</div>
										) : null}
										<SolarGradeInput
											field={mapData}
											sectionName={data.name}
											keyedSolarGradeData={keyedSolarGradeData}
											api={api}
											token={token}
											projectName={projectName}
											templateName={templateName}
											templateTypeId={templateTypeId}
											setLastModifiedOn={setLastModifiedOn}
											projectId={projectId}
											odooId={odooId}
											selectedOdooModel={selectedOdooModel}
											odooInstallationName={odooInstallationName}
											odooParentName={odooParentName}
											initialThumbnailPathes={initialThumbnailPathes}
											solarGradeIds={solarGradeIds}
											stage={stage}
											isGuest={isGuest}
											keyedKrummySolarGradeData={keyedKrummySolarGradeData}
										/>
										{aerialMarkersReady && mapData.require_aerial_view_marker && !aerialMarkerFieldIds.includes(mapData.id) ? (
											<div className="flex items-center gap-2 bg-amber-400 p-2 mt-2 rounded-lg">
												<FaMapMarkerAlt color="black" className="text-3xl" />
												<p>An aerial view marker is required. Add a marker in Aerial View.</p>
											</div>
										) : null}
									</div>
								);
							}

							return (
								<FieldConverter
									conditionalData={conditionalData}
									setConditionalData={setConditionalData}
									templateName={templateName}
									allFields={fields}
									sectionId={sectionId}
									projectId={projectId}
									templateId={templateId}
									fieldData={fieldData}
									setFieldData={setFieldData}
									projectName={projectName}
									key={mapData.id}
									data={mapData}
									api={api}
									token={token}
									sectionName={data.name}
									stage={stage}
									templateTypeId={templateTypeId}
									odooMappings={odooMappings}
									odooId={odooId}
									setRequiredFieldsRemaining={setRequiredFieldsRemaining}
									setAerialMarkersMissingRemaining={setAerialMarkersMissingRemaining}
									aerialMarkersReady={aerialMarkersReady}
									showThisField={fieldMatchesActiveFilters(mapData.id)}
									showThisSection={showThisSection}
									initialThumbnailPathes={initialThumbnailPathes}
									setLastModifiedOn={setLastModifiedOn}
									selectedOdooModel={selectedOdooModel}
									setLastUpdatedBy={setLastUpdatedBy}
									odooInstallationName={odooInstallationName}
									odooParentName={odooParentName}
									isGuest={isGuest}
									startingPendingImages={startingPendingImages}
									focusedFieldId={focusedFieldId}
									hasAerialView={hasAerialView}
									hasAerialMarker={aerialMarkerFieldIds.includes(mapData.id)}
									onShowOnAerial={onShowOnAerial}
									onFieldDataChange={onFieldDataChange}
								/>
							);
						})
					) : isLoading ? (
						<div className="flex flex-col items-center justify-center w-full my-2">
							<LoadingCircle />
							<p>Loading Fields</p>
						</div>
					) : (
						<p>This section does not have fields</p>
					)}
				</div>
			}
		</div>
	);
};

export default ProjectSection;
